// Geometría del "frente de batalla" del mapa en vivo. Cada trazo de un corredor
// se suaviza y se muestrea; la zona conquistada se arma como polígono con bordes
// irregulares (tipo carta militar), con tapa redonda en las ciudades y un frente
// dentado donde termina la conquista. Todo en unidades del mapa (sin zoom).

const TAU = Math.PI * 2
// Irregularidad de los bordes, como fracción del semiancho.
const JAG = 0.22
// Dientes del frente y cuánto avanzan (fracción del semiancho).
const TEETH = 5
const TOOTH = 0.45

const chaikin = (pts, iterations) => {
  let out = pts
  for (let k = 0; k < iterations && out.length >= 3; k++) {
    const next = [out[0]]
    for (let i = 0; i < out.length - 1; i++) {
      const [x0, y0] = out[i]
      const [x1, y1] = out[i + 1]
      next.push([0.75 * x0 + 0.25 * x1, 0.75 * y0 + 0.25 * y1], [0.25 * x0 + 0.75 * x1, 0.25 * y0 + 0.75 * y1])
    }
    next.push(out[out.length - 1])
    out = next
  }
  return out
}

// Trazo suavizado y remuestreado cada `step`, con tangente en cada muestra.
export const buildTrack = (pts, step) => {
  const smooth = chaikin(pts, 3)
  const cum = [0]
  for (let i = 1; i < smooth.length; i++) {
    cum.push(cum[i - 1] + Math.hypot(smooth[i][0] - smooth[i - 1][0], smooth[i][1] - smooth[i - 1][1]))
  }
  const length = cum[cum.length - 1]
  const n = Math.max(2, Math.ceil(length / step) + 1)
  const samples = []
  let j = 0
  for (let i = 0; i < n; i++) {
    const s = (length * i) / (n - 1)
    while (j < cum.length - 2 && cum[j + 1] < s) j++
    const t = (s - cum[j]) / (cum[j + 1] - cum[j] || 1)
    samples.push({ s, x: smooth[j][0] + (smooth[j + 1][0] - smooth[j][0]) * t, y: smooth[j][1] + (smooth[j + 1][1] - smooth[j][1]) * t })
  }
  samples.forEach((p, i) => {
    const a = samples[Math.max(0, i - 1)]
    const b = samples[Math.min(n - 1, i + 1)]
    const len = Math.hypot(b.x - a.x, b.y - a.y) || 1
    p.tx = (b.x - a.x) / len
    p.ty = (b.y - a.y) / len
  })
  return { samples, length }
}

export const sampleAt = (track, s) => {
  const { samples } = track
  if (s <= 0) return samples[0]
  if (s >= track.length) return samples[samples.length - 1]
  let i = 1
  while (i < samples.length - 1 && samples[i].s < s) i++
  const a = samples[i - 1]
  const b = samples[i]
  const t = (s - a.s) / (b.s - a.s || 1)
  const tx = a.tx + (b.tx - a.tx) * t
  const ty = a.ty + (b.ty - a.ty) * t
  const len = Math.hypot(tx, ty) || 1
  return { s, x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, tx: tx / len, ty: ty / len }
}

// Semilla estable por territorio para que los bordes no cambien entre renders.
export const seedOf = str => {
  let h = 2166136261
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return (h >>> 0) / 4294967295
}

const makeNoise = (seed, wavelength) => s => {
  const u = (s / wavelength) * TAU
  return 0.55 * Math.sin(u + seed * 6.3) + 0.3 * Math.sin(u * 2.3 + seed * 17.1) + 0.15 * Math.sin(u * 5.1 + seed * 41.7)
}

const fmt = pts => pts.map(p => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ')

// Polígono conquistado entre `from` y `to` (distancia sobre el trazo). En cada
// punta va una tapa redonda (ciudad) o un frente dentado.
const conquestShape = (track, from, to, halfW, seed, frontAtStart, frontAtEnd) => {
  const nl = makeNoise(seed, halfW * 3)
  const nr = makeNoise(seed + 0.37, halfW * 3)
  const pts = [sampleAt(track, from), ...track.samples.filter(p => p.s > from && p.s < to), sampleAt(track, to)]
  // Normal izquierda = (-ty, tx).
  const left = pts.map(p => {
    const w = halfW * (1 + JAG * nl(p.s))
    return [p.x - p.ty * w, p.y + p.tx * w]
  })
  const right = pts.map(p => {
    const w = halfW * (1 + JAG * nr(p.s))
    return [p.x + p.ty * w, p.y - p.tx * w]
  })

  const cap = (p, l, r, dir) => {
    // Media vuelta de `l` a `r` pasando por el lado de `dir` (+1 adelante, -1 atrás).
    const cx = (l[0] + r[0]) / 2
    const cy = (l[1] + r[1]) / 2
    const radius = Math.hypot(l[0] - r[0], l[1] - r[1]) / 2
    const nx = (l[0] - cx) / (radius || 1)
    const ny = (l[1] - cy) / (radius || 1)
    const out = []
    for (let i = 1; i < 10; i++) {
      const a = (Math.PI * i) / 10
      out.push([cx + radius * (Math.cos(a) * nx + Math.sin(a) * p.tx * dir), cy + radius * (Math.cos(a) * ny + Math.sin(a) * p.ty * dir)])
    }
    return out
  }

  const teeth = (p, l, r, dir) => {
    const out = []
    for (let j = 1; j < TEETH * 2; j++) {
      const u = j / (TEETH * 2)
      const push = (j % 2 ? TOOTH : -TOOTH * 0.25) * halfW * dir
      out.push([l[0] + (r[0] - l[0]) * u + p.tx * push, l[1] + (r[1] - l[1]) * u + p.ty * push])
    }
    return out
  }

  const first = pts[0]
  const last = pts[pts.length - 1]
  const l0 = left[0]
  const r0 = right[0]
  const l1 = left[left.length - 1]
  const r1 = right[right.length - 1]

  const endEdge = frontAtEnd ? teeth(last, l1, r1, 1) : cap(last, l1, r1, 1)
  const startEdge = frontAtStart ? teeth(first, r0, l0, -1) : cap(first, r0, l0, -1)
  const polygon = [...left, ...endEdge, ...[...right].reverse(), ...startEdge]

  const front = frontAtEnd
    ? { line: fmt([l1, ...endEdge, r1]), at: [last.x + last.tx * TOOTH * halfW, last.y + last.ty * TOOTH * halfW] }
    : frontAtStart
      ? { line: fmt([r0, ...startEdge, l0]), at: [first.x - first.tx * TOOTH * halfW, first.y - first.ty * TOOTH * halfW] }
      : null

  return { points: fmt(polygon), front }
}

/**
 * Conquista sobre un trazo: se pinta desde las dos puntas hacia el medio.
 * @param {{samples, length}} track
 * @param {number} p        dominación 0..1
 * @param {number} halfW    semiancho de la región
 * @param {number} seed
 * @returns {{ polygons: string[], fronts: {line: string, at: number[]}[], swords: number[][] }}
 */
export const conquestOnTrack = (track, p, halfW, seed) => {
  const empty = { polygons: [], fronts: [], swords: [] }
  if (!track || p <= 0) return empty
  const L = track.length
  if (p >= 1) return { polygons: [conquestShape(track, 0, L, halfW, seed, false, false).points], fronts: [], swords: [] }

  // Una ruta recién lanzada arranca como un puesto chico en cada ciudad que se
  // ensancha hasta el ancho completo al llegar al 25%.
  const w = halfW * (0.5 + 0.5 * Math.min(1, p * 4))
  const reach = Math.min(L * 0.46, Math.max(w * 0.35, (p / 2) * L))
  const a = conquestShape(track, 0, reach, w, seed, false, true)
  const b = conquestShape(track, L - reach, L, w, seed + 0.5, true, false)
  const fronts = [a.front, b.front]
  // Si los dos frentes quedan cerca, una sola batalla en el medio.
  const gap = L - 2 * reach
  const mid = sampleAt(track, L / 2)
  const swords = gap < halfW * 6 ? [[mid.x, mid.y]] : fronts.map(f => f.at)
  return { polygons: [a.points, b.points], fronts, swords }
}
